/**
 * Closing Disclosure View - Main Container
 *
 * Composes all Wave 2 components into a comprehensive customer-friendly
 * closing disclosure display with 8 progressive sections.
 *
 * Route Name: 'overview' (for menu integration)
 */

import React, { FC, useMemo, useState, useEffect } from 'react';
import {
  Grid,
  CircularProgress,
  Button,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { NewOrderType } from '../../../types/order';
import './print-styles.css';
import { LEDGER_CODE_PURCHASE_PRICE, LEDGER_CODE_LOAN_AMOUNT } from '../../../utils/constants';
import { logger } from '../../../utils/logger';

import RoleSummaryCards from 'components/closing-disclosure/role-summary-cards';
import PropertyMap from 'components/closing-disclosure/property-map';
import ParticipantsCard from 'components/closing-disclosure/participants-card';
import { BalancingTable } from 'components/closing-disclosure/balancing-table';
import { LoanFundingTable } from 'components/closing-disclosure/loan-funding-table';
import EscrowSection from 'components/closing-disclosure/escrow-section';
import CommissionsSection from 'components/closing-disclosure/commissions-section';
import EarnestSection from 'components/closing-disclosure/earnest-section';
import ErrorBoundary from 'components/closing-disclosure/error-boundary';
import CostBreakdownStackedBar from 'components/closing-disclosure/cost-breakdown-stacked-bar';
import CostBreakdownPieCharts from 'components/closing-disclosure/cost-breakdown-pie-charts';

import { ClosingDisclosureData } from '../../../types/closing-disclosure';
import { calculateEscrowTotal } from '../../../services/closing-disclosure-calculator';
import { getCostBreakdownByRole } from '../../../services/cost-breakdown-calculator';

const useStyles = makeStyles((theme: any) => ({
  pageContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  printButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  printButton: {
    marginRight: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
  },
  errorContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  errorPaper: {
    padding: theme.spacing(3),
    backgroundColor: `${theme.palette.error.main}14`,
    borderLeft: `4px solid ${theme.palette.error.main}`,
    marginBottom: theme.spacing(2),
  },
  emptyStatePaper: {
    padding: theme.spacing(3),
    backgroundColor: `${theme.palette.info.main}14`,
    borderLeft: `4px solid ${theme.palette.info.main}`,
    marginBottom: theme.spacing(2),
  },
  retryButton: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  loadingText: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  sectionSpacing: {
    marginBottom: theme.spacing(3),
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    width: '100%',
  },
  tabPanelEmpty: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  gridItemNoOverflow: {
    minWidth: 0,
  },
  tabLabelStyle: {
    textTransform: 'none',
    '&.Mui-selected': { color: theme.palette.text.primary },
  },
  errorActionsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  errorTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
  },
  '@media print': {
    pageContainer: {
      padding: 0,
      backgroundColor: 'white',
    },
    printButtonContainer: {
      display: 'none',
    },
    sectionSpacing: {
      pageBreakInside: 'avoid',
      marginBottom: theme.spacing(2),
    },
  } as any,
}));

interface ClosingDisclosureViewProps {
	order: NewOrderType;
	isLoading?: boolean;
	error?: string | null;
	onRetry?: () => void;
}

export const ClosingDisclosureView: FC<ClosingDisclosureViewProps> = ({
  order,
  isLoading = false,
  error = null,
  onRetry,
}) => {
  const { t } = useTranslation('closingDisclosure');
  const classes = useStyles();

  // State for tabs - initialize with default value 0
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const closingDisclosureData: ClosingDisclosureData = useMemo(() => {
    if (!order) return {} as ClosingDisclosureData;

    // Get all estate addresses (comma-separated)
    const propertyAddresses = order.estates
      ?.map((estate) => estate.address?.address)
      .filter((addr) => addr)
      .join(', ') || '';

    const closingDate = order.configuration?.closing_date || '';

    const accounting = Array.isArray(order.accounting) ? order.accounting : [];
    const purchasePriceDollars = accounting.find((entry) => entry.code === LEDGER_CODE_PURCHASE_PRICE)?.amount || 0;
    const loanAmountDollars = accounting.find((entry) => entry.code === LEDGER_CODE_LOAN_AMOUNT)?.amount || 0;

    const purchasePriceCents = Math.round(purchasePriceDollars * 100);
    const loanAmountCents = Math.round(loanAmountDollars * 100);

    // Calculate escrow using the calculator function
    const escrowData = calculateEscrowTotal(order);

    return {
      order_id: order.order_id || '',
      property_address: propertyAddresses,
      closing_date: closingDate,
      purchase_price_cents: purchasePriceCents,
      loan_amount_cents: loanAmountCents,
      role_summaries: [],
      charge_sections: [],
      escrow_total_cents: escrowData.total_cents,
      escrow_detail: {
        insurance_cents: escrowData.insurance_cents,
        taxes_cents: escrowData.taxes_cents,
      },
    } as ClosingDisclosureData;
  }, [order]);

  // Store earnest and commissions separately since they're not part of ClosingDisclosureData type
  const earnestCentsValue = useMemo(() => {
    if (!order) return 0;
    const accounting = Array.isArray(order.accounting) ? order.accounting : [];
    let earnestCents = 0;
    accounting.forEach((entry) => {
      const code = (entry.code || '').toLowerCase();
      if (code.includes('earnest') && entry.kind === 'charge') {
        earnestCents += entry.amount || 0;
      }
    });
    return Math.round(earnestCents * 100);
  }, [order]);

  const commissionsCentsValue = useMemo(() => {
    if (!order) return 0;
    const accounting = Array.isArray(order.accounting) ? order.accounting : [];
    let commissionsCents = 0;
    accounting.forEach((entry) => {
      const code = (entry.code || '').toLowerCase();
      const description = (entry.description || '').toLowerCase();
      if (code.includes('commission') || description.includes('commission')) {
        commissionsCents += entry.amount || 0;
      }
    });
    return Math.round(commissionsCents * 100);
  }, [order]);

  const costBreakdown = useMemo(() => {
    return getCostBreakdownByRole(order);
  }, [order]);

  // Calculate visibility flags for Details tab sections
  const hasEscrow = closingDisclosureData.escrow_total_cents > 0;
  const hasCommissions = commissionsCentsValue > 0;
  const hasEarnest = earnestCentsValue > 0;
  const hasAnyDetails = hasEscrow || hasCommissions || hasEarnest;

  // Check if cost breakdown data is available
  const hasBreakdownData = costBreakdown.buyer.categories.length > 0 || costBreakdown.seller.categories.length > 0;

  const activeTabValue = hasAnyDetails ? tabValue : 1;

  // Switch to Cost Breakdown tab if Details becomes empty
  if (!hasAnyDetails && tabValue === 0) {
    setTabValue(1);
  }

  if (isLoading) {
    return (
      <Grid container className={classes.pageContainer}>
        <Grid item xs={12} className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="body1" className={classes.loadingText}>
            {t('ui-loading-message') || 'Loading closing disclosure...'}
          </Typography>
          <Typography variant="caption" color="textSecondary" className={classes.loadingText}>
            {t('ui-loading') || 'Please wait while we prepare your information.'}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container className={classes.pageContainer}>
        <Grid item xs={12} className={classes.errorContainer}>
          <Paper className={classes.errorPaper}>
            <Typography variant="h6" className={classes.errorTitle} color="error">
              {t('ui-error-loading') || 'Unable to Load Closing Disclosure'}
            </Typography>
            <Typography className={classes.errorMessage} color="textSecondary">
              {error || t('ui-error-generic') || 'Something went wrong. Please try again.'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t('ui-retry-message') || 'If the problem continues, please contact support.'}
            </Typography>
          </Paper>
          <div className={classes.errorActionsContainer}>
            {onRetry && (
            <Button
              variant="contained"
              color="primary"
              onClick={onRetry}
              className={classes.retryButton}
            >
              {t('ui-retry') || 'Try Again'}
            </Button>
            )}
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => { window.location.href = '/'; }}
              className={classes.retryButton}
            >
              {t('ui-back') || 'Go Back'}
            </Button>
          </div>
        </Grid>
      </Grid>
    );
  }

  if (!order || !order.order_id) {
    return (
      <Grid container className={classes.pageContainer}>
        <Grid item xs={12} className={classes.errorContainer}>
          <Paper className={classes.emptyStatePaper}>
            <Typography variant="h6" className={classes.errorTitle} color="textSecondary">
              {t('ui-error-missing-order') || 'No Order Found'}
            </Typography>
            <Typography className={classes.errorMessage} color="textSecondary">
              {t('ui-empty-state') || 'Please go back and select a valid order.'}
            </Typography>
          </Paper>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => { window.location.href = '/'; }}
          >
            {t('ui-back') || 'Go Back'}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <ErrorBoundary
      onError={(err) => {
			  if (process.env.NODE_ENV !== 'production') {
			    logger.error('ClosingDisclosure render error:', err);
			  }
      }}
    >
      <Grid container direction="column" className={classes.pageContainer}>
        {/* Responsive layout: xs=12 stacked, sm two-column (left+tabs | balancing/funding row), md 3-column (left | center | right) */}
        <Grid item xs={12} className={classes.sectionSpacing}>
          <Grid container spacing={3}>
            {/* Left Rail: xs={12} sm={6} md={3} - contains PropertyMap + Participants */}
            <Grid item xs={12} sm={6} md={3}>
              <PropertyMap order={order} />
              <Box mt={1}>
                <ParticipantsCard order={order} />
              </Box>
            </Grid>

            {/* Center: xs={12} sm={6} md={6} - Tabs + Details/Cost Breakdown */}
            <Grid item xs={12} sm={6} md={6}>
              <Tabs
                value={activeTabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                className={classes.tabsContainer}
                aria-label="closing disclosure tabs"
              >
                {hasAnyDetails && <Tab label={t('details-tab')} value={0} className={classes.tabLabelStyle} />}
                <Tab label={t('cost-breakdown-title')} value={1} className={classes.tabLabelStyle} />
              </Tabs>

              {activeTabValue === 1 && (
              <Box className={classes.tabPanel}>
                {hasBreakdownData ? (
                  <Grid container direction="column" spacing={2}>
                    <Grid item xs={12} className={classes.gridItemNoOverflow}>
                      <CostBreakdownStackedBar
                        buyerCosts={costBreakdown.buyer.categories}
                        sellerCosts={costBreakdown.seller.categories}
                      />
                    </Grid>
                    <Grid item xs={12} className={classes.gridItemNoOverflow}>
                      <CostBreakdownPieCharts
                        buyerCosts={costBreakdown.buyer.categories}
                        sellerCosts={costBreakdown.seller.categories}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Box className={classes.tabPanelEmpty}>
                    <Typography variant="body2">
                      {t('waiting-for-breakdown-data')}
                    </Typography>
                  </Box>
                )}
              </Box>
              )}

              {activeTabValue === 0 && (
              <Box className={classes.tabPanel}>
                <Grid container direction="column" spacing={2}>
                  {hasEscrow && (
                  <Grid item xs={12} className={classes.gridItemNoOverflow}>
                    <EscrowSection
                      escrowTotal={closingDisclosureData.escrow_detail?.insurance_cents === undefined ? 0 : closingDisclosureData.escrow_total_cents}
                      escrowBreakdown={{
													  insurance: closingDisclosureData.escrow_detail?.insurance_cents || 0,
													  taxes: closingDisclosureData.escrow_detail?.taxes_cents || 0
                      }}
                    />
                  </Grid>
                  )}
                  {hasCommissions && (
                  <Grid item xs={12} className={classes.gridItemNoOverflow}>
                    <CommissionsSection commissionsTotal={commissionsCentsValue} />
                  </Grid>
                  )}
                  {hasEarnest && (
                  <Grid item xs={12} className={classes.gridItemNoOverflow}>
                    <EarnestSection earnestAmount={earnestCentsValue} />
                  </Grid>
                  )}
                </Grid>
              </Box>
              )}
            </Grid>

            {/* Right Rail: xs={12} sm={12} md={3} - contains Balancing + Loan Funding */}
            <Grid item xs={12} sm={12} md={3}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={12}>
                  <BalancingTable order={order} />
                </Grid>
                {closingDisclosureData?.loan_amount_cents > 0 && (
                <Grid item xs={12} sm={12} md={12}>
                  <LoanFundingTable order={order} />
                </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Full Width: Role Summary */}
        <Grid item xs={12} className={classes.sectionSpacing}>
          <RoleSummaryCards order={order} />
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};

export const CLOSING_DISCLOSURE_ROUTE_NAME = 'overview';

export default ClosingDisclosureView;
